import React, { useMemo, useState, useRef, useEffect } from 'react';
import { countries } from '../utils/phoneCanonical';

function CountryPicker({ value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  const selected = useMemo(
    () => countries.find((c) => c.iso2 === value) || countries.find((c) => c.iso2 === 'AU'),
    [value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.displayCode.includes(q) ||
        c.callingCode.includes(q.replace('+', '')) ||
        c.iso2.toLowerCase().includes(q)
    );
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (country) => {
    onChange(country);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 border rounded bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[140px]"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-lg leading-none" aria-hidden>
          {selected.flagEmoji}
        </span>
        <span className="font-medium">{selected.displayCode}</span>
        <span className="text-gray-500 text-sm truncate hidden sm:inline">
          {selected.name}
        </span>
        <span className="ml-auto text-gray-400">▾</span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-72 max-h-80 bg-white border rounded-lg shadow-lg flex flex-col">
          <div className="p-2 border-b">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country..."
              className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <ul className="overflow-y-auto flex-1" role="listbox">
            {filtered.map((country) => (
              <li key={country.iso2}>
                <button
                  type="button"
                  role="option"
                  aria-selected={country.iso2 === selected.iso2}
                  onClick={() => handleSelect(country)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-blue-50 ${
                    country.iso2 === selected.iso2 ? 'bg-blue-50' : ''
                  }`}
                >
                  <span className="text-lg">{country.flagEmoji}</span>
                  <span className="font-medium w-12">{country.displayCode}</span>
                  <span className="text-sm text-gray-700 truncate">{country.name}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-sm text-gray-500 text-center">No countries found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default CountryPicker;
